import React, { useMemo } from 'react';
import { cx } from 'emotion';
import { Moment } from 'moment';
import { isPrevMonth, isNextMonth, isMomentValid } from '../utils';
import { useStyles } from './styles';

export interface CalendarProps {
  days: number[][];
  activeYear: number;
  activeMonth: number;
  activeDay: number;
  minMoment?: Moment;
  maxMoment?: Moment;
  getNewMoment: (day: number, prevMonth: boolean, nextMonth: boolean) => Moment;
  onChange: (moment: Moment) => void;
}

export default function Calendar({
  days,
  activeYear,
  activeMonth,
  activeDay,
  minMoment,
  maxMoment,
  getNewMoment,
  onChange
}: CalendarProps) {
  const styles = useStyles();
  const header = useMemo(
    () => (
      <thead>
        <tr>
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(w => (
            <td key={w} className={styles.week}>
              {w}
            </td>
          ))}
        </tr>
      </thead>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <table className={styles.calendar}>
      {header}
      <tbody>
        {days.map((row, week) => (
          <tr key={week}>
            {row.map(day => {
              const isPrev = isPrevMonth(week, day);
              const isNext = isNextMonth(week, day);
              const newMoment = getNewMoment(day, isPrev, isNext);
              const notThisMonth = isPrev || isNext;
              const active =
                newMoment.year() === activeYear &&
                newMoment.month() === activeMonth &&
                newMoment.date() === activeDay;
              const disabled =
                isMomentValid(newMoment, minMoment, maxMoment) !== 0;
              const onClick = !disabled ? () => onChange(newMoment) : undefined;
              return (
                <td
                  key={day}
                  className={cx(styles.day, {
                    [styles.notThisMonth]: notThisMonth || disabled,
                    [styles.active]: active,
                    [styles.disabled]: disabled
                  })}
                  onClick={onClick}
                >
                  {day}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
